/**
 * Patient search for the command palette (Ctrl+K, and `/` for patients
 * only): name, chart number, phone (4 digits is enough), birthdate or email,
 * matched on the server's blind index. Registered under the shell's
 * `patients` provider id, so it replaces the shell's default.
 */
import type { PatientSearchHit } from '@velora/contracts/patients';
import { ApiError, getShellPatients } from '@velora/sdk';
import {
  clientFor,
  registerSearchProvider,
  setCurrentPatient,
  type SearchProvider,
} from '../../app/shell.js';
import { selectLabel } from './select-patient.js';
import { searchPatients } from './api.js';
import { listName, usDate } from './format.js';
import { createElement } from 'react';
import { GradeBadge } from './grade/GradeBadge.js';

export const PATIENT_ROUTE = '/patients';

/** Client-side navigation from outside React (the palette runs results directly). */
export function goTo(path: string): void {
  if (typeof window === 'undefined') return;
  const url = new URL(path, window.location.origin);
  const office = new URLSearchParams(window.location.search).get('office');
  if (office && !url.searchParams.has('office')) url.searchParams.set('office', office);
  window.history.pushState(window.history.state, '', `${url.pathname}${url.search}`);
  window.dispatchEvent(new PopStateEvent('popstate', { state: window.history.state }));
}

export const recordPath = (id: string) => `${PATIENT_ROUTE}/${id}`;

const MATCH_HINT: Record<PatientSearchHit['matchedOn'], string> = {
  name: '',
  phone: 'phone match',
  dob: 'birthdate match',
  chart: 'chart match',
  email: 'email match',
};

export function hitHint(h: PatientSearchHit, opts: { dob?: boolean } = {}): string {
  return [
    opts.dob !== false && h.birthdate
      ? `${usDate(h.birthdate)}${h.age !== null ? ` (${h.age})` : ''}`
      : null,
    // Chart numbers are not synced from PulseSync v1: show the PMS id (PAT-08).
    h.chartNumber ? `#${h.chartNumber}` : h.pmsId ? `PMS id ${h.pmsId}` : null,
    h.primaryPhoneLast4 ? `ends ${h.primaryPhoneLast4}` : null,
    h.status !== 'active' ? h.status.replace('_', ' ') : null,
    MATCH_HINT[h.matchedOn] || null,
  ]
    .filter(Boolean)
    .join(' · ');
}

export function patientSearchProvider(navigate: (path: string) => void = goTo): SearchProvider {
  return {
    id: 'patients',
    heading: 'Patients',
    order: 0,
    patients: true,
    minLength: 2,
    permission: 'patient.view',
    search: async (q, { signal, office }) => {
      let hits: PatientSearchHit[];
      try {
        // Inactive and archived patients are included (listed after active ones,
        // with their status in the hint): a caller matched to an archived record
        // must be findable by name (2026-10-21).
        hits = await searchPatients({ q, limit: 8, includeInactive: true }, signal, office);
      } catch (e) {
        // Without the patients API (demo mode, a stack where only the shell
        // answers) use the shell's search; it fails the same way if the
        // session itself is gone.
        if (!(e instanceof ApiError) || (e.status !== 404 && e.status !== 401)) throw e;
        const res = await getShellPatients(clientFor(office), { query: { q, limit: 8 }, signal });
        hits = res.hits.map((h) => ({
          ...h,
          practiceId: '',
          status: h.status as PatientSearchHit['status'],
          primaryPhoneLast4: null,
          householdId: null,
          age: null,
          matchedOn: 'name' as const,
          hasCriticalAlert: false,
        }));
      }
      return hits.map((h) => ({
        id: h.id,
        label: listName(h),
        hint: hitHint(h),
        icon: 'user-round',
        phi: true,
        office: h,
        // Hover only: Enter / click on the row opens the patient.
        adornment: createElement(GradeBadge, {
          patientId: h.id,
          place: 'search',
          interactive: false,
        }),
        run: () => {
          // Picking a patient selects them (the top bar's patient), with
          // what the search already returned; the record page fills the rest.
          setCurrentPatient({
            id: h.id,
            label: selectLabel(h),
            chartNumber: h.chartNumber,
            age: h.age,
            homeLocationId: h.homeLocationId,
            ...(h.hasCriticalAlert ? { hasAlert: true } : {}),
          });
          navigate(recordPath(h.id));
        },
      }));
    },
  };
}

let installed = false;
let unregister: (() => void) | null = null;

/** Puts this provider on top of the `patients` id (last registration wins). */
function assertProvider(): void {
  unregister?.();
  unregister = registerSearchProvider(patientSearchProvider());
}

function typing(target: EventTarget | null): boolean {
  const el = target as HTMLElement | null;
  return !!el && (el.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(el.tagName));
}

/**
 * Registers the provider when the manifest loads. The shell registers its
 * default `patients` provider later (after sign-in), and the newest
 * registration wins, so the provider is re-asserted right before the palette
 * opens (Ctrl+K, `/`, or the top-bar trigger). Requested in
 * docs/requests/patients-shell-search-provider.md so the shell skips its
 * default when this module is installed; this then becomes a no-op.
 */
export function installPatientSearch(): void {
  if (installed || typeof window === 'undefined') return;
  installed = true;
  assertProvider();
  window.addEventListener(
    'keydown',
    (e) => {
      if (
        ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') ||
        (e.key === '/' && !typing(e.target))
      ) {
        assertProvider();
      }
    },
    true,
  );
  window.addEventListener(
    'pointerdown',
    (e) => {
      if ((e.target as Element | null)?.closest?.('.vel-palette-trigger')) assertProvider();
    },
    true,
  );
}
